// App Store page. Source: the platform App Store (localhost:4321/en/app-store, October 2026):
// 3 platform apps and the 10 public calculation apps with their token cost.

const app = (title, tokens, fr, en) => ({ title, tokens, fr, en });

const calculationApps = {
  ec1: [
    app('EC1-Vent', 1, 'Pressions dynamiques de pointe, coefficient CsCd et coefficients de pression pour chaque type de paroi selon la NF EN 1991-1-4.', 'Peak velocity pressure, CsCd factor and pressure coefficients for each wall type to NF EN 1991-1-4.'),
  ],
  ec2: [
    app('EC2-Dalle', 1, 'Dalles rectangulaires chargées perpendiculairement à leur plan.', 'Rectangular slabs loaded perpendicular to their plane.'),
    app('EC2-Ferraillage', 1, 'Ferraillage à l’Eurocode 2 d’une section rectangulaire en béton armé.', 'Eurocode 2 reinforcement of a rectangular reinforced concrete section.'),
    app('EC2-Flèche', 1, 'Flèche d’une poutre ou d’une console de section rectangulaire ou en T.', 'Deflection of a beam or cantilever with a rectangular or T section.'),
    app('EC2-Koka', 1, 'Poutre continue selon la méthode de l’Eurocode 2.', 'Continuous beam using the Eurocode 2 method.'),
    app('EC2-Tige', 1, 'Charge critique ultime ou état d’équilibre d’un poteau en béton armé chargé en tête.', 'Ultimate critical load or equilibrium state of a reinforced concrete column loaded at the top.'),
  ],
  ec3: [
    app('EC3-Section I', 1, 'Classe du profilé laminé ou reconstitué soudé, résistance (moment, effort normal, effort tranchant), déversement et flambement.', 'Class of the rolled or welded section, resistance (bending, axial, shear), lateral-torsional buckling and buckling.'),
  ],
  ec5: [
    app('EC5-Poteau', 4, 'Vérification de la tenue d’un poteau en bois.', 'Check of a timber column.'),
    app('EC5-Poutres', 1, 'Contraintes et flèches des pannes et des poutres en bois.', 'Stresses and deflections of timber purlins and beams.'),
  ],
  ec7: [
    app('EC7-Mur', 1, 'Mur de soutènement aux Eurocodes.', 'Retaining wall to the Eurocodes.'),
  ],
};

const tokenLabel = { fr: (n) => `${n} token${n > 1 ? 's' : ''}`, en: (n) => `${n} token${n > 1 ? 's' : ''}` };
const cards = (key, locale) => calculationApps[key].map((a) => ({
  icon: 'Calculate', kicker: tokenLabel[locale](a.tokens), title: a.title, text: a[locale],
}));

const tabs = (locale) => {
  const labels = locale === 'fr'
    ? { ec1: 'Eurocode 1 — Actions', ec2: 'Eurocode 2 — Béton', ec3: 'Eurocode 3 — Acier', ec5: 'Eurocode 5 — Bois', ec7: 'Eurocode 7 — Géotechnique' }
    : { ec1: 'Eurocode 1 — Actions', ec2: 'Eurocode 2 — Concrete', ec3: 'Eurocode 3 — Steel', ec5: 'Eurocode 5 — Timber', ec7: 'Eurocode 7 — Geotechnics' };
  const intro = locale === 'fr'
    ? { ec1: 'Actions sur les structures : vent.', ec2: 'Calcul des structures en béton armé.', ec3: 'Calcul des structures en acier.', ec5: 'Calcul des structures en bois.', ec7: 'Calcul géotechnique.' }
    : { ec1: 'Actions on structures: wind.', ec2: 'Design of reinforced concrete structures.', ec3: 'Design of steel structures.', ec5: 'Design of timber structures.', ec7: 'Geotechnical design.' };
  return Object.keys(calculationApps).map((key) => ({ label: labels[key], text: intro[key], items: cards(key, locale) }));
};

const fr = {
  seo: {
    title: 'App Store TriDyme | Applications de calcul Eurocodes en ligne et applications terrain',
    description: 'Terra (application terrain hors ligne), Structure 3D (éléments finis), Telecom BIM et 10 applications de calcul Eurocodes (EC1, EC2, EC3, EC5, EC7) accessibles dans le navigateur.',
  },
  hero: {
    badge: 'Norma · App Store',
    title: 'Les applications de la plateforme ',
    highlight: 'et vos calculs Eurocodes',
    subtitle: 'Les applications propres à la plateforme, dont l’application mobile de terrain, puis les applications de calcul publiées sur le portail développeurs TriDyme. Elles s’ouvrent dans le navigateur et enregistrent vos modèles sur la plateforme.',
    ctas: [
      { label: 'Ouvrir la plateforme', to: 'https://platform.tridyme.com/', style: 'primary' },
      { label: 'Voir les tarifs', to: '/pricing/', style: 'outline' },
    ],
    stats: [
      { value: '3', label: 'applications de la plateforme' },
      { value: '{{appCount}}', label: 'applications de calcul publiques' },
      { value: '5', label: 'Eurocodes couverts' },
    ],
    image: { src: '/img/platform/appstore', alt: 'App Store de la plateforme TriDyme' },
  },
  sections: [
    {
      id: 'plateforme',
      tag: 'Applications de la plateforme',
      title: 'Du terrain au modèle numérique',
      blocks: [
        {
          type: 'cards',
          columns: 3,
          items: [
            {
              icon: 'PhotoCamera',
              kicker: 'Terrain · mobile · hors ligne',
              title: 'Terra',
              text: 'L’application des équipes sur site. Chaque tâche planifiée sur la plateforme arrive sur le téléphone ; ce qui est relevé sur place revient, même sans réseau.',
              list: [
                'Visites techniques et formulaires, remplis à la voix',
                'Photos, relevés et contrôle d’inventaire',
                'Levée des réserves et maintenance',
                'Hors ligne, synchronisé au retour du réseau',
              ],
            },
            {
              icon: 'Norma',
              kicker: 'Éléments finis · toutes structures',
              title: 'Structure 3D',
              text: 'Calcul de structure aux éléments finis pour tout type d’ouvrage : bâtiments, charpentes, passerelles, pylônes… Sur la plateforme, elle reçoit aussi le modèle du pylône construit depuis l’audit documentaire.',
              list: [
                'Modélisation 3D : nœuds, barres, sections, matériaux, appuis',
                'Cas de charges et combinaisons ELU / ELS, vent (NF EN 1991-1-4)',
                'Analyses statique et modale, taux de travail aux Eurocodes',
                'Pylônes télécom : modèle généré depuis la NDC et le DOE',
              ],
            },
            {
              icon: 'ViewInAr',
              kicker: 'Maquette numérique · sites télécom',
              title: 'Telecom BIM',
              text: 'Le modeleur BIM dédié aux sites télécom : pylône, antennes et zone au sol, placés d’après les plans, à partir d’un catalogue d’équipements propre au métier. La maquette se consulte dans le navigateur et s’échange en IFC.',
              list: [
                'Catalogue de familles d’équipements télécom (antennes, RRU, FH, baies, shelters…)',
                'Zone au sol calée sur le plan du DOE (zones techniques, clôture, dalles)',
                'Export IFC avec les propriétés de chaque équipement',
                'Plans de coupe et d’élévation (SVG, PDF)',
              ],
            },
          ],
        },
        {
          type: 'screens',
          columns: 2,
          items: [
            { src: '/img/platform/structure3d', title: 'Structure 3D', caption: 'Modèle éléments finis d’un pylône treillis.', alt: 'Modèle 3D d’un pylône treillis dans Structure 3D' },
            { src: '/img/platform/bim', title: 'Telecom BIM', caption: 'Pylône, équipements et zone au sol d’un site.', alt: 'Maquette BIM d’un site télécom dans Telecom BIM' },
          ],
        },
      ],
    },
    {
      id: 'calcul',
      tone: 'light',
      tag: 'Applications de calcul TriDyme',
      title: '{{appCount}} applications Eurocodes, prêtes à l’emploi',
      subtitle: 'Chaque application s’ouvre dans le navigateur ou directement dans la plateforme, avec vos modèles enregistrés. Le coût de chaque calcul est indiqué en tokens.',
      blocks: [
        { type: 'tabs', items: tabs('fr') },
      ],
    },
    {
      id: 'tarifs',
      tag: 'Tarifs',
      title: 'Payez à l’usage',
      blocks: [
        {
          type: 'cards',
          columns: 2,
          items: [
            { icon: 'Payments', title: 'Tokens', text: 'Un calcul consomme 1 token (4 pour EC5-Poteau). Les formules d’abonnement incluent un volume de tokens.', to: '/pricing/', linkLabel: 'Voir les tarifs' },
            { icon: 'Code', title: 'Kits de développement', text: 'Des kits de développement permettent à vos équipes de créer et publier leurs propres applications de calcul. Accès sur demande.', to: '/contact/', linkLabel: 'Nous contacter' },
          ],
        },
      ],
    },
  ],
  cta: {
    title: 'Lancez votre premier calcul',
    text: 'Ouvrez la plateforme et choisissez une application.',
    ctas: [
      { label: 'Ouvrir la plateforme', to: 'https://platform.tridyme.com/', style: 'black' },
      { label: 'Parler à un expert', to: '/contact/', style: 'outline' },
    ],
  },
};

const en = {
  seo: {
    title: 'TriDyme App Store | Online Eurocode calculation apps and field apps',
    description: 'Terra (offline field app), Structure 3D (finite elements), Telecom BIM and 10 Eurocode calculation apps (EC1, EC2, EC3, EC5, EC7) in your browser.',
  },
  hero: {
    badge: 'Norma · App Store',
    title: 'The platform’s apps ',
    highlight: 'and your Eurocode calculations',
    subtitle: 'The platform’s own apps, including the mobile field app, then the engineering apps published on the TriDyme developer portal. They open in the browser and save your models on the platform.',
    ctas: [
      { label: 'Open the platform', to: 'https://platform.tridyme.com/', style: 'primary' },
      { label: 'See pricing', to: '/pricing/', style: 'outline' },
    ],
    stats: [
      { value: '3', label: 'platform apps' },
      { value: '{{appCount}}', label: 'public calculation apps' },
      { value: '5', label: 'Eurocodes covered' },
    ],
    image: { src: '/img/platform/appstore', alt: 'TriDyme platform App Store' },
  },
  sections: [
    {
      id: 'plateforme',
      tag: 'Platform apps',
      title: 'From the field to the digital model',
      blocks: [
        {
          type: 'cards',
          columns: 3,
          items: [
            {
              icon: 'PhotoCamera',
              kicker: 'Field · mobile · offline',
              title: 'Terra',
              text: 'The on-site crews’ app. Every task planned on the platform lands on the phone; what is recorded on site flows back, even without network.',
              list: [
                'Site surveys and forms, filled in by voice',
                'Photos, measurements and inventory checks',
                'Punch-list and maintenance',
                'Offline, synced when the network is back',
              ],
            },
            {
              icon: 'Norma',
              kicker: 'Finite elements · any structure',
              title: 'Structure 3D',
              text: 'Finite-element structural analysis for any kind of structure: buildings, frames, footbridges, towers… On the platform it also receives the tower model built from the document audit.',
              list: [
                '3D modelling: nodes, members, sections, materials, supports',
                'Load cases and ULS / SLS combinations, wind (EN 1991-1-4)',
                'Static and modal analysis, Eurocode utilisation ratios',
                'Telecom towers: model generated from the design note and as-built file',
              ],
            },
            {
              icon: 'ViewInAr',
              kicker: 'Digital model · telecom sites',
              title: 'Telecom BIM',
              text: 'The BIM modeller built for telecom sites: tower, antennas and ground area, placed from the drawings, using a catalogue of telecom-specific equipment. The model is viewed in the browser and exchanged as IFC.',
              list: [
                'Catalogue of telecom equipment families (antennas, RRUs, microwave links, cabinets, shelters…)',
                'Ground area fitted to the as-built drawing (equipment zones, fence, slabs)',
                'IFC export with each item’s properties',
                'Section and elevation drawings (SVG, PDF)',
              ],
            },
          ],
        },
        {
          type: 'screens',
          columns: 2,
          items: [
            { src: '/img/platform/structure3d', title: 'Structure 3D', caption: 'Finite element model of a lattice tower.', alt: '3D model of a lattice tower in Structure 3D' },
            { src: '/img/platform/bim', title: 'Telecom BIM', caption: 'Tower, equipment and ground area of a site.', alt: 'BIM model of a telecom site in Telecom BIM' },
          ],
        },
      ],
    },
    {
      id: 'calcul',
      tone: 'light',
      tag: 'TriDyme calculation apps',
      title: '{{appCount}} Eurocode apps, ready to use',
      subtitle: 'Each app opens in the browser or directly in the platform, with your saved models. The cost of each calculation is shown in tokens.',
      blocks: [
        { type: 'tabs', items: tabs('en') },
      ],
    },
    {
      id: 'tarifs',
      tag: 'Pricing',
      title: 'Pay as you go',
      blocks: [
        {
          type: 'cards',
          columns: 2,
          items: [
            { icon: 'Payments', title: 'Tokens', text: 'A calculation uses 1 token (4 for EC5-Poteau). Subscription plans include a volume of tokens.', to: '/pricing/', linkLabel: 'See pricing' },
            { icon: 'Code', title: 'Development kits', text: 'Development kits let your teams build and publish their own calculation apps. Access on request.', to: '/contact/', linkLabel: 'Contact us' },
          ],
        },
      ],
    },
  ],
  cta: {
    title: 'Run your first calculation',
    text: 'Open the platform and pick an app.',
    ctas: [
      { label: 'Open the platform', to: 'https://platform.tridyme.com/', style: 'black' },
      { label: 'Talk to an expert', to: '/contact/', style: 'outline' },
    ],
  },
};

module.exports = { fr, en };
